import { Component, OnDestroy, ViewChild } from '@angular/core';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { NavController, NavParams } from 'ionic-angular';
import { LockedDayService } from '../../../../services/locked-day/locked-day.service';
import { DateService } from '../../../../services/date/date.service';
import { DailyLogService } from '../../../../services/daily-log/daily-log.service';
import { AppDataSyncService } from '../../../../services/app-data-sync/app-data-sync.service';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { LockDayFilters } from '../../../../models/lock-day.models';
import { ConsumablesService } from '../../../../services/consumables/consumables.service';
import { ConsumableEditor, PreviousDayTotal } from '../../../../models/consumables/consumables.models';
import {
  ActionType,
  ErrorType,
  ValidationError,
  PageValidationType,
  ValidationResult
} from '../../../../models/projects/validation.models';
import { catchError, debounce } from 'rxjs/operators';
import { timer } from 'rxjs/observable/timer';
import { ConsumablePermissions, LockedDayPermissions } from '../../../../models/permissions/permission.models';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { CompleteDayService } from '../../../../services/complete-day/complete-day.service';
import { NavHelperService } from '../../../../services/nav/nav-helper.service';
import { cloneDeep } from 'lodash';
import { ImplementerService } from '../../../../services/implementer/implementer.service';
import { DailyLogDatePickerComponent } from '../../../shared/components/daily-log-date-picker/daily-log-date-picker';
import { AppConfig } from '../../../../app/app.config';
import { VesselService } from '../../../../services/vessel/vessel.service';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-consumable-edit-component',
  templateUrl: 'consumable-edit-component.component.html'
})
export class ConsumableEditComponentComponent implements OnDestroy {
  @ViewChild('dailyLogDatePickerComponent') dailyLogDatePickerComponent: DailyLogDatePickerComponent;

  config = AppConfig.settings;

  consumableEditor: ConsumableEditor = new ConsumableEditor();
  dailyLogDate: PtLuxonDate = new PtLuxonDate();

  validationResult: ValidationResult = new ValidationResult();

  $saveRequestedChange: Subject<boolean> = new Subject<boolean>();
  saveDebounceSubscription: Subscription;

  loading: boolean;
  validationLoading: boolean;
  private savingConsumables: boolean = false;
  private pendingConsumablesSave: boolean = false;

  dayLockedByDate: boolean = false;
  lockable: boolean = false;
  dayCompleted: boolean;
  isImplementerActiveForDay: boolean; // when true the day will be open for editing regardless of dayLockedByDate

  firstLoadComplete: boolean = false;
  dateBeforeInitialSetup: boolean;

  public mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];

  vesselTracksUreaUsage: boolean = false;

  foodWasteType: 'wet' | 'dry' = 'wet';
  remainingFuelOil: number;
  remainingLubeOil: number;
  remainingPotableWater: number;
  remainingUrea: number;
  remainingJackingGrease: number;

  permissions: ConsumablePermissions = new ConsumablePermissions();
  lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();

  currentSetupConfigured: boolean;
  isNonJackingVessel: boolean = false;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public lockedDayService: LockedDayService,
    public dateService: DateService,
    public dailyLogService: DailyLogService,
    public consumableService: ConsumablesService,
    private appDataSyncService: AppDataSyncService,
    private permissionService: PermissionsService,
    private completeDayService: CompleteDayService,
    private navHelperService: NavHelperService,
    private implementerService: ImplementerService,
    private vesselService: VesselService
  ) {
    this.navHelperService.currentSetupConfigured.subscribe(currentSetupConfigured => {
      this.currentSetupConfigured = currentSetupConfigured;
    });
  }

  async ngOnInit() {
    this.loading = true;
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    this.isNonJackingVessel = await this.vesselService.isCurrentVesselNonJacking();
    await this.ionViewEnter();
    await this.updateDataEntryVariables();
    await this.vesselService.getEditor().then(v => {
      this.vesselTracksUreaUsage = v.tracksUreaUsage;
    });
  }

  async ionViewEnter() {
    this.validationLoading = true;
    await this.loadData(true);
    this.setupAutoSaveDebounce();

    this.permissions = await this.permissionService.consumablePermissions();
    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);
  }

  ngOnDestroy(): void {
    if (this.saveDebounceSubscription) {
      this.saveDebounceSubscription.unsubscribe();
    }
  }

  setupAutoSaveDebounce() {
    if (this.saveDebounceSubscription) {
      this.saveDebounceSubscription.unsubscribe();
    }

    this.saveDebounceSubscription = this.$saveRequestedChange
      .pipe(
        catchError(err => {
          throw (err.error);
        }),
        debounce(() => timer(500)))
      .subscribe(val => {
        this.validationLoading = true;
        this.saveConsumablesData(val);
      });
  }

  queueAutoSave(isComplete: boolean) {
    if (this.loading || !this.firstLoadComplete || this.dailyLogDatePickerComponent?.dateChangeLoading) {
      return;
    }

    this.$saveRequestedChange.next(isComplete);
  }

  // one method for all - that checks dayCompleted, implementer status, etc, and sets canEnterData flag
  async updateDataEntryVariables() {
    await Promise.all([
      this.checkImplementerActive(),
      this.checkDayLocked(),
      this.permissionService.updateLockedDayFunctionalPermissions(this.lockedDayPermissions, this.dailyLogDate)
    ]);

    await this.setCanEnterDataFlag();
  }

  async setCanEnterDataFlag() {
    if (!this.dayCompleted
      && ((this.config.isImplementerEnvironment && this.isImplementerActiveForDay) || (!this.config.isImplementerEnvironment && this.dayLockedByDate))) {
      this.navHelperService.updateCanEnterData(true);
    } else {
      this.navHelperService.updateCanEnterData(false);
    }
  }

  async checkDayLocked() {
    const filters: LockDayFilters = {
      date: this.dailyLogDate.dateTime,
      userId: null,
      history: null
    };

    const lockedSummary = await this.lockedDayService.checkDay(filters);

    this.dayLockedByDate = lockedSummary.lockedByDate;
    this.lockable = lockedSummary.lockable;

    this.completeDayService.checkDay(filters).then(result => {
      this.dayCompleted = result;
    });
  }

  async dayUnlockedForEditing() {
    this.loading = true;
    await this.checkDayLocked();
    this.loading = false;
  }

  async changesCancelled() {
    this.firstLoadComplete = false;
    this.loading = true;
    await this.loadData(true);
  }

  async changesSaved() {
    await this.loadData(false);
  }

  public async saveConsumablesData(isComplete: boolean) {
    if (this.savingConsumables) {
      this.pendingConsumablesSave = true;
      return;
    }

    this.clearData();
    this.calculateRemaining();
    this.validationLoading = true;
    this.savingConsumables = true;

    if (this.isNonJackingVessel) {
      this.validationLoading = false;
      this.savingConsumables = false;
      return;
    }

    if ((!this.config.isImplementerEnvironment && !this.dayLockedByDate)
      || ((this.config.isImplementerEnvironment && !this.isImplementerActiveForDay))) {
      this.validationLoading = false;
      this.savingConsumables = false;
      return;
    }

    try {
      const editorToSave = cloneDeep(this.consumableEditor);
      editorToSave.date = cloneDeep(this.dailyLogDate);
      editorToSave.isCompleted = isComplete;

      const savedEditor = await this.consumableService.saveEditor(editorToSave);
      this.consumableEditor.id = savedEditor.id;
      this.consumableEditor.isCompleted = isComplete;

      this.calculateRemaining();
      await this.consumableService.saveConsumableChangeMultipleRecords([this.consumableEditor]);
      this.validationResult = new ValidationResult();
    } catch (error) {
      if (this.isValidationSaveError(error)) {
        this.validationResult = new ValidationResult();
        this.applyValidationErrors(error);
        this.consumableEditor.isCompleted = false;
      } else {
        throw error;
      }
    } finally {
      this.validationLoading = false;
      this.savingConsumables = false;

      if (this.pendingConsumablesSave) {
        this.pendingConsumablesSave = false;
        await this.saveConsumablesData(false);
      }
    }
  }

  markConsumablesComplete() {
    this.saveConsumablesData(true);
  }

  clearData() {
    if (this.consumableEditor.loadedFuelOil == null || this.consumableEditor.loadedFuelOil.toString() === '') {
      this.consumableEditor.loadedFuelOil = null;
      this.consumableEditor.fuelOilDensity = null;
    }
  }

  async loadData(refresh: boolean, dateChange?: boolean) {
    if (refresh) {
      this.loading = true;
      this.consumableEditor = new ConsumableEditor();
      this.consumableEditor.date = this.dailyLogDate;
    }

    // refresh daily log date, if user has changed the date
    if (dateChange) {
      this.firstLoadComplete = false;
      this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
      await this.updateDataEntryVariables();
    }

    await this.checkDayLocked();

    if (refresh) {
      await this.appDataSyncService.updateAppDataForDate(this.dailyLogDate);

      this.validationResult = new ValidationResult();
      this.validationLoading = true;

      this.consumableEditor = await this.consumableService.getConsumableDataForDay(this.dailyLogDate);
      this.calculateRemaining();
      this.firstLoadComplete = true;
      this.loading = false;
      await this.vesselService.getEditor().then(v => {
        this.consumableEditor.tracksUreaUsage = v.tracksUreaUsage;
      });

      if (this.isNonJackingVessel) {
        this.validationLoading = false;
        return;
      }

      this.consumableService.validate(this.consumableEditor).subscribe(() => {
          this.validationLoading = false;
        }, error => {
          if (this.isValidationSaveError(error)) {
            this.applyValidationErrors(error);
            this.consumableEditor.isCompleted = false;
            this.validationLoading = false;
          }
        });
    } else {
      this.calculateRemaining();
      this.loading = false;
    }
  }

  convertForAddition(value: number, precision: number): number {
    const precisionConverter = Math.pow(10, precision);
    return Math.round(value * precisionConverter);
  }

  convertAfterAddition(value: number, precision: number): number {
    if (value && value !== 0) {
      const precisionConverter = Math.pow(10, precision);
      return (value / precisionConverter);
    }

    return value;
  }

  calculateRemaining() {
    if (this.consumableEditor) {
      const precision = 4;

      if (!this.consumableEditor.previousDay) {
        this.consumableEditor.previousDay = new PreviousDayTotal();
      }

      let fuelOilRemaining = this.convertForAddition(+this.consumableEditor.previousDay.previousFuelOil, precision);

      if (this.consumableEditor.loadedFuelOil) {
        fuelOilRemaining = fuelOilRemaining + this.convertForAddition(+this.consumableEditor.loadedFuelOil, precision);
      }

      if (this.consumableEditor.transferredFuelOil) {
        fuelOilRemaining = fuelOilRemaining + this.convertForAddition(+this.consumableEditor.transferredFuelOil, precision);
      }

      if (this.consumableEditor.usedFuelOil) {
        fuelOilRemaining = fuelOilRemaining - this.convertForAddition(+this.consumableEditor.usedFuelOil, precision);
      }
      if (this.consumableEditor.adjustRemainingOnBoardFuelOilTotal) {
        fuelOilRemaining = fuelOilRemaining + this.convertForAddition(+this.consumableEditor.adjustRemainingOnBoardFuelOilTotal, precision);
      }

      this.remainingFuelOil = this.convertAfterAddition(fuelOilRemaining, precision);

      let lubeOilRemaining = this.convertForAddition(this.consumableEditor.previousDay.previousLubeOil, precision);

      if (this.consumableEditor.loadedLubeOil) {
        lubeOilRemaining = lubeOilRemaining + this.convertForAddition(+this.consumableEditor.loadedLubeOil, precision);
      }

      if (this.consumableEditor.transferredLubeOil) {
        lubeOilRemaining = lubeOilRemaining + this.convertForAddition(+this.consumableEditor.transferredLubeOil, precision);
      }

      if (this.consumableEditor.usedLubeOil) {
        lubeOilRemaining = lubeOilRemaining - this.convertForAddition(+this.consumableEditor.usedLubeOil, precision);
      }
      if (this.consumableEditor.adjustRemainingOnBoardLubeOilTotal) {
        lubeOilRemaining = lubeOilRemaining + this.convertForAddition(+this.consumableEditor.adjustRemainingOnBoardLubeOilTotal, precision);
      }

      this.remainingLubeOil = this.convertAfterAddition(lubeOilRemaining, precision);


      let potableWaterRemaining = this.convertForAddition(this.consumableEditor.previousDay.previousPotableWater, precision);

      if (this.consumableEditor.loadedPotableWater) {
        potableWaterRemaining = potableWaterRemaining + this.convertForAddition(+this.consumableEditor.loadedPotableWater, precision);
      }

      if (this.consumableEditor.madePotableWater) {
        potableWaterRemaining = potableWaterRemaining + this.convertForAddition(+this.consumableEditor.madePotableWater, precision);
      }

      if (this.consumableEditor.usedPotableWater) {
        potableWaterRemaining = potableWaterRemaining - this.convertForAddition(+this.consumableEditor.usedPotableWater, precision);
      }
      if (this.consumableEditor.adjustRemainingOnBoardPotableWaterTotal) {
        potableWaterRemaining = potableWaterRemaining +
          this.convertForAddition(+this.consumableEditor.adjustRemainingOnBoardPotableWaterTotal, precision);
      }

      this.remainingPotableWater = this.convertAfterAddition(potableWaterRemaining, precision);


      let ureaRemaining = this.convertForAddition(this.consumableEditor.previousDay.previousUrea, precision);

      if (this.consumableEditor.loadedUrea) {
        ureaRemaining = ureaRemaining + this.convertForAddition(+this.consumableEditor.loadedUrea, precision);
      }

      if (this.consumableEditor.usedUrea) {
        ureaRemaining = ureaRemaining - this.convertForAddition(+this.consumableEditor.usedUrea, precision);
      }

      if (this.consumableEditor.adjustRemainingOnBoardUreaTotal) {
        ureaRemaining = ureaRemaining + this.convertForAddition(+this.consumableEditor.adjustRemainingOnBoardUreaTotal, precision);
      }

      this.remainingUrea = this.convertAfterAddition(ureaRemaining, precision);


      let jackingGreaseRemaining = this.convertForAddition(this.consumableEditor.previousDay.previousJackingGrease, precision);

      if (this.consumableEditor.loadedJackingGrease) {
        jackingGreaseRemaining = jackingGreaseRemaining + this.convertForAddition(+this.consumableEditor.loadedJackingGrease, precision);
      }

      if (this.consumableEditor.usedJackingGrease) {
        jackingGreaseRemaining = jackingGreaseRemaining - this.convertForAddition(+this.consumableEditor.usedJackingGrease, precision);
      }

      if (this.consumableEditor.adjustRemainingOnBoardJackingGreaseTotal) {
        jackingGreaseRemaining = jackingGreaseRemaining + this.convertForAddition(+this.consumableEditor.adjustRemainingOnBoardJackingGreaseTotal, precision);
      }

      this.remainingJackingGrease = this.convertAfterAddition(jackingGreaseRemaining, precision);
    }
  }

  regexChange(inputTime: string) {
    if (inputTime.charAt(0) === '2') {
      this.mask[1] = new RegExp('[0-3]');
    } else {
      this.mask[1] = new RegExp('\\d');
    }

    this.queueAutoSave(false);
  }

  otherPagesNotComplete(): boolean {
    if (this.validationResult.errors.some(e => e.pageValidationType != null
      && e.pageValidationType !== PageValidationType.DailyObservations && e.pageValidationType !== PageValidationType.FieldPositioning)) {
      return true;
    }

    return false;
  }

  // isImplementerEnvironment variable only tells us if the environment is the implementer, we still need to check if current date falls between
  // snapshotSummary start and end dates, if it does then we know this date is editable
  async checkImplementerActive() {
    this.isImplementerActiveForDay = await this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate));
  }

  dateBeforeInitialSetupChanged($event: boolean) {
    this.dateBeforeInitialSetup = $event;
  }

  goToActiveLogDay() {
    this.dailyLogDatePickerComponent.goToActiveLogDay();
  }

  dateLoadingStarted() {
    this.firstLoadComplete = false;
  }

  dateLoadingFinished() {
    this.firstLoadComplete = true;
  }

  isFieldRequired(field: string): boolean {
    switch (field) {
      case 'usedFuelOil':
      case 'usedLubeOil':
      case 'usedPotableWater':
        return true;
      case 'usedUrea':
        return this.newConsumableRulesActive() && this.consumableEditor?.tracksUreaUsage !== false;
      case 'usedJackingGrease':
        return this.newConsumableRulesActive();
      case 'reasonForAdjustmentFuelOil':
        return this.hasNonZeroValue(this.consumableEditor?.adjustRemainingOnBoardFuelOilTotal);
      case 'reasonForAdjustmentLubeOil':
        return this.hasNonZeroValue(this.consumableEditor?.adjustRemainingOnBoardLubeOilTotal);
      case 'reasonForAdjustmentPotableWater':
        return this.hasNonZeroValue(this.consumableEditor?.adjustRemainingOnBoardPotableWaterTotal);
      case 'reasonForAdjustmentUrea':
        return this.consumableEditor?.tracksUreaUsage !== false
          && this.hasNonZeroValue(this.consumableEditor?.adjustRemainingOnBoardUreaTotal);
      case 'reasonForAdjustmentJackingGrease':
        return this.hasNonZeroValue(this.consumableEditor?.adjustRemainingOnBoardJackingGreaseTotal);
      case 'fuelOilDensity':
        return this.hasNonZeroValue(this.consumableEditor?.loadedFuelOil);
      case 'loadedFuelOil':
        return this.hasNonZeroValue(this.consumableEditor?.fuelOilDensity);
      case 'foodWasteType':
        return this.hasPositiveValue(this.consumableEditor?.foodWaste);
      default:
        return false;
    }
  }

  fieldLabelClasses(field: string) {
    const required = this.isFieldRequired(field);

    return {
      'required-field-label': required
    };
  }

  fieldRowClasses(field: string) {
    const required = this.isFieldRequired(field);
    const invalid = this.hasValidationError(field);

    return {
      'required-field-row': required,
      'validation-failed': invalid
    };
  }

  private isValidationSaveError(error: any): boolean {
    return (error?.status === 412 || error?.status === 400) && !!error?.error?.errors?.length;
  }

  private applyValidationErrors(error: any) {
    const errors = (error?.error?.errors || []) as ValidationError[];

    this.validationResult.errors = this.validationResult.errors.concat(
      errors.filter(p =>
        this.validationResult.errors.filter(er =>
          er.message === p.message).length === 0));

    this.validationResult.errors.forEach(consumableError => {
      if (consumableError.pageValidationType == null) {
        consumableError.pageValidationType = PageValidationType.Consumables;
        consumableError.type = ErrorType.Error;
        consumableError.actionType = ActionType.Consumable;
      }
    });

    this.validationResult.isValid = error?.error?.isValid === undefined ? false : error.error.isValid;
  }

  private hasValidationError(field: string): boolean {
    const expectedProperty = this.normalizeValidationValue(field);

    if (!expectedProperty) {
      return false;
    }

    return (this.validationResult?.errors || []).some(error => {
      return this.normalizeValidationValue(error?.property) === expectedProperty;
    });
  }

  private normalizeValidationValue(value: string): string {
    return (value || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  }

  private hasNonZeroValue(value: number | null | undefined): boolean {
    const normalizedValue = this.normalizeNumericValue(value);
    return normalizedValue != null && normalizedValue !== 0;
  }

  private hasPositiveValue(value: number | null | undefined): boolean {
    const normalizedValue = this.normalizeNumericValue(value);
    return normalizedValue != null && normalizedValue > 0;
  }

  private normalizeNumericValue(value: number | string | null | undefined): number | null {
    if (value == null || value === '') {
      return null;
    }

    const normalizedValue = Number(value);
    return Number.isNaN(normalizedValue) ? null : normalizedValue;
  }

  private newConsumableRulesActive(): boolean {
    const configCutoff = (this.config as any)?.newConsumableRulesFromUtc;
    const currentDate = this.dailyLogDate?.dateTime?.toUTC?.();

    if (configCutoff && currentDate) {
      const cutoff = configCutoff instanceof DateTime
        ? configCutoff
        : DateTime.fromISO(configCutoff, { zone: 'utc' });

      if (cutoff.isValid) {
        return currentDate >= cutoff;
      }
    }

    return true;
  }
}



// WEBPACK FOOTER //
// ./src/pages/DPR Pages/consumables/consumable-edit-component/consumable-edit-component.component.ts